'use client';

import { type Outage } from '@/lib/outages-db';
import { X } from 'lucide-react';
import { useEffect } from 'react';

interface OutageDetailModalProps {
  outage: Outage;
  onClose: () => void;
}

const severityColors = {
  critical: 'bg-red-500/10 border-red-500 text-red-400',
  major: 'bg-accent-500/10 border-accent text-accent',
  moderate: 'bg-yellow-500/10 border-yellow-500 text-yellow-400',
};

const statusColors = {
  ongoing: 'bg-red-500/10 text-red-400',
  investigating: 'bg-yellow-500/10 text-yellow-400',
  resolved: 'bg-green-500/10 text-green-400',
};

export function OutageDetailModal({ outage, onClose }: OutageDetailModalProps) {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const duration = outage.ended_at
    ? Math.round((new Date(outage.ended_at).getTime() - new Date(outage.started_at).getTime()) / (1000 * 60 * 60))
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      {/* Modal Container */}
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-background border border-gray-200 dark:border-border rounded-lg shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-600 dark:text-foreground-tertiary hover:text-accent transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-cream/5"
        >
          <X size={24} />
        </button>

        {/* Content */}
        <div className="p-8">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-accent font-bold text-2xl">{outage.vendor}</span>
              <span className={`px-3 py-1 text-sm rounded border ${severityColors[outage.severity]}`}>
                {outage.severity}
              </span>
              <span className={`px-3 py-1 text-sm rounded ${statusColors[outage.status]}`}>
                {outage.status}
              </span>
            </div>
            <h2 className="text-gray-900 dark:text-foreground text-3xl font-bold mb-2">{outage.title}</h2>
            <p className="text-gray-700 dark:text-foreground-secondary text-lg">{outage.description}</p>
          </div>

          {/* Timeline */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 dark:bg-cream/5 rounded-lg border border-gray-200 dark:border-border-muted">
            <div>
              <div className="text-gray-600 dark:text-foreground-tertiary text-sm mb-1">Started</div>
              <div className="text-gray-900 dark:text-foreground font-semibold">
                {new Date(outage.started_at).toLocaleString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZoneName: 'short',
                })}
              </div>
            </div>
            {outage.ended_at && (
              <>
                <div>
                  <div className="text-gray-600 dark:text-foreground-tertiary text-sm mb-1">Ended</div>
                  <div className="text-gray-900 dark:text-foreground font-semibold">
                    {new Date(outage.ended_at).toLocaleString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZoneName: 'short',
                    })}
                  </div>
                </div>
                {duration !== null && (
                  <div>
                    <div className="text-gray-600 dark:text-foreground-tertiary text-sm mb-1">Duration</div>
                    <div className="text-gray-900 dark:text-foreground font-semibold">{duration} hours</div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Impact Details */}
          <div className="mb-6">
            <h3 className="text-gray-900 dark:text-foreground text-xl font-semibold mb-4">Impact</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-cream/5 rounded-lg border border-gray-200 dark:border-border-muted">
                <div className="text-gray-600 dark:text-foreground-tertiary text-sm mb-1">Scope</div>
                <div className="text-gray-900 dark:text-foreground font-semibold">{outage.impact_scope}</div>
              </div>
              {outage.estimated_affected_customers && (
                <div className="p-4 bg-gray-50 dark:bg-cream/5 rounded-lg border border-gray-200 dark:border-border-muted">
                  <div className="text-gray-600 dark:text-foreground-tertiary text-sm mb-1">Affected Customers</div>
                  <div className="text-gray-900 dark:text-foreground font-semibold">
                    {Number(outage.estimated_affected_customers).toLocaleString()}
                  </div>
                </div>
              )}
              {outage.estimated_financial_impact_usd && (
                <div className="p-4 bg-gray-50 dark:bg-cream/5 rounded-lg border border-gray-200 dark:border-border-muted">
                  <div className="text-gray-600 dark:text-foreground-tertiary text-sm mb-1">Financial Impact</div>
                  <div className="text-gray-900 dark:text-foreground font-semibold">
                    ${(Number(outage.estimated_financial_impact_usd) / 1000000).toLocaleString()}M USD
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Affected Services */}
          {outage.affected_services && outage.affected_services.length > 0 && (
            <div className="mb-6">
              <h3 className="text-gray-900 dark:text-foreground text-xl font-semibold mb-3">Affected Services</h3>
              <div className="flex flex-wrap gap-2">
                {outage.affected_services.map((service) => (
                  <span
                    key={service}
                    className="px-3 py-1 bg-accent-muted text-accent rounded border border-border"
                  >
                    {service}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* RCA Summary */}
          {outage.rca_published && outage.rca_summary && (
            <div className="mb-6">
              <h3 className="text-gray-900 dark:text-foreground text-xl font-semibold mb-3">Root Cause Analysis</h3>
              <div className="p-4 bg-gray-50 dark:bg-cream/5 rounded-lg border border-gray-200 dark:border-border-muted">
                <p className="text-gray-700 dark:text-foreground-secondary leading-relaxed mb-3">{outage.rca_summary}</p>
                {outage.root_cause_category && (
                  <div className="inline-block px-3 py-1 bg-accent-muted text-accent text-sm rounded border border-border">
                    Category: {outage.root_cause_category}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Links */}
          <div className="mb-6">
            <h3 className="text-gray-900 dark:text-foreground text-xl font-semibold mb-3">Resources</h3>
            <div className="flex flex-wrap gap-3">
              {outage.status_page_url && (
                <a
                  href={outage.status_page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gray-100 dark:bg-cream/5 text-gray-900 dark:text-foreground hover:bg-gray-200 dark:hover:bg-cream/10 border border-gray-300 dark:border-border hover:border-accent rounded transition-colors"
                >
                  Status Page →
                </a>
              )}
              {outage.rca_url && (
                <a
                  href={outage.rca_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gray-100 dark:bg-cream/5 text-gray-900 dark:text-foreground hover:bg-gray-200 dark:hover:bg-cream/10 border border-gray-300 dark:border-border hover:border-accent rounded transition-colors"
                >
                  RCA/Postmortem →
                </a>
              )}
              {outage.postmortem_url && outage.postmortem_url !== outage.rca_url && (
                <a
                  href={outage.postmortem_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gray-100 dark:bg-cream/5 text-gray-900 dark:text-foreground hover:bg-gray-200 dark:hover:bg-cream/10 border border-gray-300 dark:border-border hover:border-accent rounded transition-colors"
                >
                  Postmortem →
                </a>
              )}
            </div>
          </div>

          {/* Tags */}
          {outage.tags && outage.tags.length > 0 && (
            <div>
              <h3 className="text-gray-900 dark:text-foreground text-xl font-semibold mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {outage.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-sm bg-gray-100 dark:bg-cream/5 text-gray-600 dark:text-foreground-tertiary rounded border border-gray-200 dark:border-border-muted"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
